import { v4 as uuid } from 'uuid'
import { Filters, ScreenerFilter } from '../../../proto/generated/screener'
import type { ScreenerFilterWithIdAndTag } from '../StockScreener'

type FilterOperandLike = {
    filterValue?: { type?: string | number; value?: string }
    filter_value?: { type?: string | number; value?: string }
    parameters?: Array<{ code?: string; value?: { value?: string } }>
    multiplier?: { type?: string | number; value?: string }
}

function operandValue(op: FilterOperandLike | undefined): string {
    if (!op) return ''
    const fv = op.filterValue ?? op.filter_value
    return String(fv?.value ?? '')
}

function operandParams(op: FilterOperandLike | undefined): string {
    if (!op?.parameters?.length) return ''
    return op.parameters
        .map((p) => `${p.code ?? ''}=${p.value?.value ?? ''}`)
        .sort()
        .join('|')
}

function operandMultiplier(op: FilterOperandLike | undefined): string {
    if (!op?.multiplier?.value) return ''
    return `${op.multiplier.type ?? ''}:${op.multiplier.value}`
}

/** Stable identity for merge/dedup — ignores id, tag, saved. */
export function filterSemanticKey(filter: ScreenerFilter): string {
    const fc = filter.filterCondition ?? (filter as { filter_condition?: typeof filter.filterCondition }).filter_condition
    if (!fc) return uuid()

    const lhs = (fc.lhsOperand ?? (fc as { lhs_operand?: FilterOperandLike }).lhs_operand) as FilterOperandLike | undefined
    const operator = String(fc.operator ?? '')
    const rhsList = (fc.rhsOperands ?? (fc as { rhs_operands?: FilterOperandLike[] }).rhs_operands ?? []) as FilterOperandLike[]

    const rhsParts = rhsList.map((rhs) => {
        const fv = rhs.filterValue ?? rhs.filter_value
        const type = String(fv?.type ?? '')
        return [type, operandValue(rhs), operandParams(rhs), operandMultiplier(rhs)].join(':')
    })

    return [operandValue(lhs), operandParams(lhs), operator, ...rhsParts.sort()].join('::')
}

export function tagFilters(filters: ScreenerFilter[]): ScreenerFilterWithIdAndTag[] {
    return filters.map((filter) => ({
        ...filter,
        id: uuid(),
        tag: true,
    }))
}

export function mergeFilters(
    base: ScreenerFilterWithIdAndTag[],
    incoming: ScreenerFilterWithIdAndTag[],
): ScreenerFilterWithIdAndTag[] {
    const merged = [...base]
    const indexByKey = new Map(merged.map((f, i) => [filterSemanticKey(f), i]))

    for (const filter of incoming) {
        const key = filterSemanticKey(filter)
        const tagged = { ...filter, id: filter.id || uuid(), tag: true as const }
        const existingIdx = indexByKey.get(key)
        if (existingIdx !== undefined) {
            merged[existingIdx] = tagged
        } else {
            indexByKey.set(key, merged.length)
            merged.push(tagged)
        }
    }
    return merged
}

export function computeTurnDelta(
    ledger: ScreenerFilterWithIdAndTag[],
    fullSet: ScreenerFilterWithIdAndTag[],
): ScreenerFilterWithIdAndTag[] {
    const ledgerKeys = new Set(ledger.map(filterSemanticKey))
    return fullSet.filter((f) => !ledgerKeys.has(filterSemanticKey(f)))
}

export function ledgersSemanticallyEqual(
    a: ScreenerFilterWithIdAndTag[],
    b: ScreenerFilterWithIdAndTag[],
): boolean {
    if (a.length !== b.length) return false
    const keysA = a.map(filterSemanticKey).sort()
    const keysB = b.map(filterSemanticKey).sort()
    return keysA.every((key, index) => key === keysB[index])
}

export function parseFiltersFromProto(raw: unknown): ScreenerFilterWithIdAndTag[] | null {
    if (!raw || typeof raw !== 'object') return null
    try {
        const filtersProto = Filters.fromJSON(raw as object)
        if (!filtersProto.filters?.length) return null
        return tagFilters(filtersProto.filters)
    } catch {
        return null
    }
}

/** Parse server `activeConversationFilters` ({ filters: [...] } or legacy nested shape). */
export function parseActiveConversationFilters(raw: unknown): ScreenerFilterWithIdAndTag[] {
    if (raw == null) return []
    const direct = parseFiltersFromProto(raw)
    if (direct?.length) return direct
    if (typeof raw === 'object' && raw !== null && 'filters' in raw) {
        const inner = (raw as { filters?: unknown }).filters
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
            return parseFiltersFromProto(inner) ?? []
        }
    }
    return []
}
